import {
  ButtonLink,
  IconButton,
  RiArrowRightLine,
  RiDeleteBinFill,
  RiFolderLine,
  RiPencilFill,
  Stack,
} from 'component-library';
import { useTranslation } from 'react-i18next';

import { Workstream } from '@/api/rest/resources/types/workstreams';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useDeleteWorkstreamAction } from '@/pages/shared/workstreams/pages/hooks/useDeleteWorkstreamAction';
import { paths } from '@/routing';
import { buildPath } from '@/utils/buildPath';

import { getWorkstreamMilestones } from '../utils/index';

type WorkstreamEditCardProps = {
  workstream: Workstream;
};

export const WorkstreamEditCard = ({ workstream }: WorkstreamEditCardProps) => {
  const { t } = useTranslation();
  const orgId = useOrganizationId();
  const { submit: deleteWorkstream, isDeleting } = useDeleteWorkstreamAction(orgId);
  const isStaff = useIsStaffUser();

  const handleDelete = () => {
    if (!isDeleting) {
      deleteWorkstream(workstream.id);
    }
  };

  const { total: milestoneCount } = getWorkstreamMilestones(workstream);

  return (
    <Stack
      direction='row'
      spacing={4}
      className='w-full border border-divider rounded-lg px-4 py-4 items-center'
      data-testid='workstream-edit-card'
    >
      <Stack className='items-center justify-center rounded-lg border border-divider p-2'>
        <RiFolderLine className='text-primary-100 text-xl' />
      </Stack>
      <Stack spacing={1} className='grow'>
        {workstream.description && (
          <span className='typography-overline text-primary-100'>{workstream.description}</span>
        )}
        <span className='typography-h3 text-text-primary'>{workstream.title}</span>
        <span className='typography-caption text-text-secondary'>
          {`${milestoneCount} ${t('global.workstream.labels.milestones')} · ${workstream.plot_groups.length} ${t('global.workstream.labels.plotGroups')}`}
        </span>
      </Stack>
      {isStaff && (
        <Stack direction='row' spacing={1}>
          <ButtonLink
            to={buildPath(paths.staff.workstreamsEdit, { pathParams: { workstreamId: workstream.id } })}
            variant='text'
            className='text-neutral-black-60 p-2 hover:bg-neutral-black-4 rounded-full'
          >
            <RiPencilFill />
          </ButtonLink>
          <IconButton onClick={handleDelete} className={isDeleting ? 'opacity-50 pointer-events-none' : ''}>
            <RiDeleteBinFill />
          </IconButton>
          <ButtonLink
            variant='text'
            className='text-neutral-black-60 p-2 hover:bg-neutral-black-4 rounded-full'
            to={buildPath(paths.staff.home, { queryParams: { workstreamId: workstream.id } })}
          >
            <RiArrowRightLine />
          </ButtonLink>
        </Stack>
      )}
    </Stack>
  );
};
